import React from 'react';
import { Link } from 'react-router-dom';
import { checkDeveloperMode } from '../../DeveloperWorkbench/DeveloperWorkbench.utils';
import { mercury } from '@bfme-technology/styled';
import type { TopBarProps } from './TopBar.types';
import { useTopBar } from './TopBar.hook';
import { formatBreadcrumbName } from './TopBar.utils';
import { NotificationDropdown } from './NotificationDropdown';
import * as styles from './TopBar.styles';

export const TopBar: React.FC<TopBarProps> = ({
  activeTab,
  theme,
  toggleTheme,
  isDevWorkbenchOpen,
  setIsDevWorkbenchOpen,
  setIsSidebarOpen,
  userName,
  userEmail,
  userInitials,
  onLogout,
  onSettingsClick,
}) => {
  const { 
    t, 
    isProfileDropdownOpen, 
    setIsProfileDropdownOpen,
    isNotificationDropdownOpen,
    setIsNotificationDropdownOpen,
    unreadCount,
    setUnreadCount,
    pathnames 
  } = useTopBar();

  return (
    <header className={`relative z-30 ${mercury.topbarClass}`}>
      <div className="flex items-center gap-3.5">
        <button 
          onClick={() => setIsSidebarOpen(true)} 
          className={styles.hamburgerBtn}
        >
          <i className="fa-solid fa-bars text-lg"></i>
        </button>
        <div className="flex flex-col gap-0.5">
          <h2 className={`capitalize ${mercury.topbarTitleClass} leading-tight`}>
            {activeTab === 'dashboard' ? 'Overview' : t(activeTab)}
          </h2>
          <div className={styles.breadcrumbContainer}>
            <Link to="/" className="hover:text-primary-accent transition-colors"><i className="fa-solid fa-house" /></Link>
            {pathnames.map((value, index) => {
              const to = `/${pathnames.slice(0, index + 1).join('/')}`;
              const isLast = index === pathnames.length - 1;
              const name = formatBreadcrumbName(value);
              
              return (
                <React.Fragment key={to}>
                  <i className="fa-solid fa-chevron-right text-[8px] opacity-50" />
                  {isLast ? (
                    <span className="text-text-primary font-semibold">{name}</span>
                  ) : (
                    <Link to={to} className="hover:text-primary-accent transition-colors">{name}</Link>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 md:gap-4">
        <div className={styles.searchContainer}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input type="text" placeholder="Search analytics..." className={styles.searchInput} readOnly />
        </div>

        <button onClick={toggleTheme} className={styles.iconBtn} title="Toggle Dark/Light Mode">
          {theme === 'dark' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"></circle>
              <line x1="12" y1="1" x2="12" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="23"></line>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
              <line x1="1" y1="12" x2="3" y2="12"></line>
              <line x1="21" y1="12" x2="23" y2="12"></line>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
            </svg>
          )}
        </button>

        {checkDeveloperMode() && (
          <button 
            id="dev-workbench-trigger"
            onClick={() => setIsDevWorkbenchOpen(!isDevWorkbenchOpen)}
            className={styles.devBtn(isDevWorkbenchOpen)}
            title="Developer Workbench"
            aria-label="Open Developer Workbench"
          >
            <i className="fa-solid fa-screwdriver-wrench text-sm"></i>
          </button>
        )}

        <div className="relative">
          <button 
            onClick={() => setIsNotificationDropdownOpen(!isNotificationDropdownOpen)} 
            className={`${styles.iconBtn} relative`} 
            title="Notifications"
            data-testid="topbar-notification-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-emerald-500 text-[10px] font-bold text-slate-950 flex items-center justify-center animate-pulse" data-testid="topbar-unread-badge">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <NotificationDropdown
            isOpen={isNotificationDropdownOpen}
            onClose={() => setIsNotificationDropdownOpen(false)}
            onUnreadCountChange={setUnreadCount}
          />
        </div>

        <div className="relative">
          <button
            onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
            className={styles.profileBtn}
            title="User Profile"
          >
            {userInitials}
          </button>
          {isProfileDropdownOpen && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setIsProfileDropdownOpen(false)}
              />
              <div className={styles.profileDropdownMenu}>
                <div className={styles.profileDropdownHeader}>
                  <div className={styles.profileDropdownAvatar}>
                    {userInitials}
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className={styles.profileDropdownName}>{userName}</span>
                    <span className={styles.profileDropdownEmail}>{userEmail}</span>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setIsProfileDropdownOpen(false);
                    onSettingsClick();
                  }}
                  className={styles.dropdownMenuItem}
                >
                  <i className="fa-solid fa-gear text-sm"></i>
                  <span>{t('settings', 'Settings')}</span>
                </button>
                <button 
                  onClick={() => {
                    setIsProfileDropdownOpen(false);
                    onLogout();
                  }}
                  className={styles.dropdownMenuLogout}
                >
                  <i className="fa-solid fa-right-from-bracket text-sm"></i>
                  <span>{t('logout', 'Logout')}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
